import {AbstractQITMeetingState, AbstractState} from "./DHTStates";

export class AfterInterviewsState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Interviews";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “We offer immunization services to everyone who visits this facility. We are already doing our jobs.”
        </p>
        <p class="my-3">
          This facility manager does not think that the vaccinator and the community should be engaging in problem solving. Instead, she prefers to get instructions from the district team.
        </p>`
      });
    }
    if (stakeholder.id === "vaccinatorB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I could see the value of working more with the community. However, doing so is not part of my job.”
        </p>
        <p class="my-3">
          This vaccinator has personally experienced how the lack of communication around immunization services has been a problem in the community. She says that she could see the benefit of working more with the community, but she is not comfortable expressing disagreement with her manager.
        </p>`
      });
    }
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: `<p class="my-3">
          Community members say that they feel like immunization services are offered at inconvenient times, and that it is hard to get clear information about health services. However, parents who have brought their children for immunization report that service quality is good.
        </p>`
      });
    }
  }
}

export class FromDataAnalysisState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Data Analysis";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “It is unfortunate that there are children who are not been vaccinated. However, if parents are not bringing their children to the facility, that is their choice.”
        </p>`
      })
    }
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0">
          You meet with the manager at this facility to review its data together.<br/><br/>
          Overall, this facility has <span class="border-b-2 border-dotted border-gray-800" data-tippy-content="For more information about analyzing facility data based on access and utilization, see the Resources section.">
          poor access but relatively good ongoing utilization of routine immunization services.</span>
        </p>`
      })
    }
  }
}

export class GoAndSeeState extends AbstractState {
  getStakeholderState (stakeholder) {}
}

export class QITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes()
      })
    }
  }
}

export class FirstSuccessfulQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "vaccinatorB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I am glad that we are trying the meetings. Community members had useful input. We will see how the evening sessions go!”
        </p>`
      })
    }
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
          <p class="my-5 font-bold">
            The facility sends the following additional notes about the meeting:
          </p>
          <ul class="my-5 lg:my-0 list-disc list-inside">
            <li>
              Various members of the community came to the meeting to discuss why immunization services are not
              being accessed.
            </li>
            <li>
              We used the <span class="border-b-2 border-dotted border-gray-800"
              data-tippy-content="For more information about these and other problem solving tools, see the Resources section.">fish-bone diagram and 5-whys</span> to
              determine that a root cause of poor access is that the timing of immunization services are seen as
              inconvenient by community members.
            </li>
            <li>
              We decided to start a <span class="whitespace-no-wrap border-b-2 border-dotted border-gray-800" data-tippy-content="PDSA stands for Plan Do Study Act. For more information about this and other problem solving tools, see the Resources section.">PDSA cycle</span> to
              try offering evening immunization sessions once per month.
            </li>
            <li>
              Community attendees agreed to share information about the session.
            </li>
          </ul>`
      })
    }
  }
}

export class FollowupQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
            <p class="my-5 font-bold">
              The facility sends the following additional notes about the meeting:
            </p>
            <ul class="my-5 lg:my-0 list-disc list-inside">
              <li>
                The evening session was well attended, but was very crowded, with long wait times.
              </li>
              <li>
                At this meeting, the community attendees requested weekly evening sessions. We do not feel like we
                have the resources to keep the facility open one evening a week.
              </li>
              <li>
                An attending community leader offered to try to find additional funding for evening staff. For now,
                they agreed to try to help us find volunteers to help at the existing evening session to see if they
                can decrease wait times.
              </li>
            </ul>`
      })
    }
  }
}

export class MicroPlanningMeetingState extends AbstractQITMeetingState {
  getTitle() {
    return "Week " + this.getWeek() + ": Micro Planning";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: this.getMicroPlanningNotes()
      })
    }
  }
}

export class WhenReaches80PercentAdoptionState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Adoption";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I will admit that the quality improvement team has been more useful than I expected.”
        </p>`
      })
    }
    if (stakeholder.id === "vaccinatorB") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I feel much more connected to the community with my work, now. We help each other, and there is much more openness and good communication when there are issues.”
        </p>`
      })
    }
    if (stakeholder.id === "communityB") {
      return super.getStakeholderState({
        data: "This facility is now regularly using quality improvement teams for problem solving and more effective micro planning, with the active input of the community."
      })
    }
  }
}
